import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';

import { Trabajo, Producto, Servicio, PhoneNumber } from '../types';
import { serviciosDetallados } from '../pages/HomePage';
import { API_BASE_URL, formatPrecio } from '../utils';

// ==================== COMPONENTE BUSCADOR INTELIGENTE ====================
export default function SearchBar({ navigateToPage }: { navigateToPage: (page: string) => void }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Cerrar resultados al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Función para normalizar texto (quitar tildes, mayúsculas, etc.)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize("NFD") // Descompone caracteres con tildes
      .replace(/[\u0300-\u036f]/g, "") // Elimina las tildes
      .replace(/[^a-z0-9\s]/g, "") // Elimina caracteres especiales
      .trim();
  };

  // Función de búsqueda inteligente
  // Función de búsqueda inteligente mejorada
  const searchMatches = (searchTerm: string, text: string): boolean => {
    const normalizedSearch = normalizeText(searchTerm);
    const normalizedText = normalizeText(text);
    
    // Si el término es muy corto (1-2 caracteres), buscar al inicio de palabras
    if (searchTerm.length <= 2) {
      const words = normalizedText.split(/\s+/);
      return words.some(word => word.startsWith(normalizedSearch));
    }
    
    // Búsqueda parcial - si cualquier parte del texto contiene el término
    if (normalizedText.includes(normalizedSearch)) return true;
    
    // Búsqueda por inicio de palabra
    const words = normalizedText.split(/\s+/);
    if (words.some(word => word.startsWith(normalizedSearch))) return true;
    
    // Búsqueda por palabras individuales del término de búsqueda
    const searchWords = normalizedSearch.split(' ').filter(w => w.length > 0);
    return searchWords.every(searchWord => 
      words.some(word => word.includes(searchWord))
    );
  };

  // Función de búsqueda con debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.length > 0) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 200);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

    const performSearch = async () => {
    setLoading(true);
    const results = [];

    // Buscar en servicios (datos locales)
    const serviciosResults = serviciosDetallados
      .filter(servicio => 
        searchMatches(searchTerm, servicio.titulo) ||
        searchMatches(searchTerm, servicio.descripcion)
      )
      .map(servicio => ({
        id: servicio.id,
        tipo: 'servicio',
        titulo: servicio.titulo,
        descripcion: servicio.descripcion.substring(0, 100) + '...',
        action: () => {
          navigateToPage('servicios');
          setTimeout(() => {
            const element = document.getElementById(`servicio-${servicio.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('highlight-animation');
              setTimeout(() => element.classList.remove('highlight-animation'), 2000);
            }
          }, 500);
          setSearchTerm('');
          setShowResults(false);
        }
      }));
    results.push(...serviciosResults);

    // Buscar en trabajos (API)
    try {
      const trabajosResponse = await fetch(`${API_BASE_URL}/trabajos`);
      if (trabajosResponse.ok) {
        const trabajosData = await trabajosResponse.json();
        const trabajosResults = (trabajosData as Trabajo[])
          .filter((trabajo) =>
            searchMatches(searchTerm, trabajo.titulo) ||
            searchMatches(searchTerm, trabajo.descripcion) ||
            searchMatches(searchTerm, trabajo.ubicacion) ||
            searchMatches(searchTerm, trabajo.categoria)
          )
          .map((trabajo) => ({
            id: trabajo.id,
            tipo: 'proyecto',
            titulo: trabajo.titulo,
            descripcion: trabajo.descripcion.substring(0, 100) + '...',
            categoria: trabajo.categoria,
            action: () => {
              navigateToPage('proyectos');
              // Abrir directamente la ficha del proyecto seleccionado
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('selectTrabajo', { detail: trabajo }));
              }, 600);
              setSearchTerm('');
              setShowResults(false);
            }
          }));
        results.push(...trabajosResults);
      }
    } catch (error) {
      console.error('Error buscando trabajos:', error);
    }

    // Buscar en productos (API) - SIN LÍMITE DE 3
    try {
      const productosResponse = await fetch(`${API_BASE_URL}/productos`);
      if (productosResponse.ok) {
        const productosData = await productosResponse.json();
        
        // NO LIMITAR CON slice(0, 3) - MOSTRAR TODOS
        const productosResults = (productosData as Producto[])
          .filter((producto) => {
            if (searchTerm.length <= 2) {
              const normalizedSearch = normalizeText(searchTerm);
              const normalizedName = normalizeText(producto.nombre);
              const words = normalizedName.split(/\s+/);
              return words.some(word => word.startsWith(normalizedSearch));
            }

            return searchMatches(searchTerm, producto.nombre) ||
                  searchMatches(searchTerm, producto.descripcion) ||
                  searchMatches(searchTerm, producto.categoria);
          })
          .map((producto) => ({
            id: producto.id,
            tipo: 'producto',
            titulo: producto.nombre,
            descripcion: producto.descripcion.substring(0, 100) + '...',
            precio: producto.precio,
            categoria: producto.categoria,
            destacado: producto.destacado,
            action: () => {
              navigateToPage('productos');
              // Abrir directamente la ficha del producto seleccionado
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('selectProducto', { detail: producto }));
              }, 600);
              setSearchTerm('');
              setShowResults(false);
            }
          }));
        
        results.push(...productosResults);
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
    }

    setSearchResults(results);
    setShowResults(results.length > 0);
    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchTerm('');
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <label htmlFor="global-search-input" className="sr-only">Buscar servicios, proyectos, productos</label>
        <input
          id="global-search-input"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length > 2 && setShowResults(true)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar servicios, proyectos..."
          className="w-64 lg:w-80 px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" aria-label="Cargando resultados"></div>
          ) : (
            <Search className="h-4 w-4 text-gray-400" aria-hidden="true" />
          )}
        </div>
      </div>

      {/* Resultados de búsqueda */}
      {showResults && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-[70vh] overflow-y-auto z-50">
          {searchResults.length > 0 ? (
            <div className="py-2">
              {/* Contador de resultados */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} para "{searchTerm}"
                </span>
              </div>
              
              {/* Agrupar por tipo */}
              {['servicio', 'proyecto', 'producto'].map(tipo => {
                const items = searchResults.filter(r => r.tipo === tipo);
                if (items.length === 0) return null;
                
                return (
                  <div key={tipo} className="mb-2">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-600 uppercase">
                          {tipo === 'servicio' ? 'Servicios' : 
                          tipo === 'proyecto' ? 'Proyectos' : 'Productos'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {items.length} resultado{items.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {items.map((result, index) => (
                      <button
                        key={`${result.tipo}-${result.id}-${index}`}
                        onClick={result.action}
                        className="w-full px-4 py-3 hover:bg-gray-50 flex items-start text-left transition-colors"
                      >
                        <div className={`mr-3 mt-0.5 p-1.5 rounded-lg ${
                          result.tipo === 'servicio' ? 'bg-blue-100' :
                          result.tipo === 'proyecto' ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          {result.tipo === 'servicio' ? (
                            <Wrench className="h-4 w-4 text-blue-600" />
                          ) : result.tipo === 'proyecto' ? (
                            <Hammer className="h-4 w-4 text-green-600" />
                          ) : (
                            <Package className="h-4 w-4 text-purple-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 text-sm">{result.titulo}</p>
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{result.descripcion}</p>
                          {result.precio && (
                            <p className="text-sm font-semibold text-blue-600 mt-1">
                              {formatPrecio(result.precio)}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No se encontraron resultados</p>
              <p className="text-gray-400 text-xs mt-1">Intenta con otros términos</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface NavbarProps {
  currentPage: string;
  navigateToPage: (page: string) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  scrolled: boolean;
}
